/**
 * PurchaseEntryChooser — entry-point modal for starting a new purchase (Task 8).
 *
 * Both views (POS / Form) produce identical post_purchase_atomic payloads, so
 * the user can switch freely between them. The last choice — and an optional
 * "always use this view" preference — are remembered in localStorage so the
 * chooser can be skipped on subsequent visits.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Zap, FileText, X, Check } from 'lucide-react'

export type PurchaseView = 'pos' | 'form'

const VIEW_KEY = 'palstro-purchase-entry-view'
const ALWAYS_KEY = 'palstro-purchase-entry-always'

const ROUTES: Record<PurchaseView, string> = {
  pos: '/purchases/new-pos',
  form: '/purchases/new',
}

/** Reads the remembered preference. `always` gates whether to skip the chooser. */
export function getPreferredPurchaseView(): { view: PurchaseView; always: boolean } {
  let view: PurchaseView = 'pos'
  let always = false
  try {
    view = localStorage.getItem(VIEW_KEY) === 'form' ? 'form' : 'pos'
    always = localStorage.getItem(ALWAYS_KEY) === 'true'
  } catch {
    /* localStorage unavailable — fall back to defaults */
  }
  return { view, always }
}

export function purchaseViewRoute(view: PurchaseView): string {
  return ROUTES[view]
}

export function PurchaseEntryChooserModal({
  open, onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const [always, setAlways] = useState(false)

  if (!open) return null

  const choose = (view: PurchaseView) => {
    try {
      localStorage.setItem(VIEW_KEY, view)
      localStorage.setItem(ALWAYS_KEY, always ? 'true' : 'false')
    } catch {
      /* ignore persistence failure — navigation still proceeds */
    }
    onClose()
    navigate(ROUTES[view])
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-full overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900">New purchase</h3>
            <p className="text-xs text-gray-500 mt-0.5">Pick how you'd like to enter it</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* POS view */}
          <button
            onClick={() => choose('pos')}
            className="group text-left rounded-2xl border-2 border-gray-200 hover:border-orange-400 hover:bg-orange-50/50 p-4 transition-colors"
          >
            <div className="h-11 w-11 rounded-xl bg-orange-100 group-hover:bg-orange-200 flex items-center justify-center mb-3 transition-colors">
              <Zap className="h-5 w-5 text-orange-600" />
            </div>
            <p className="text-sm font-bold text-gray-900">POS view</p>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Fast entry, ideal for cash purchases and market buys.
            </p>
          </button>

          {/* Form view */}
          <button
            onClick={() => choose('form')}
            className="group text-left rounded-2xl border-2 border-gray-200 hover:border-orange-400 hover:bg-orange-50/50 p-4 transition-colors"
          >
            <div className="h-11 w-11 rounded-xl bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center mb-3 transition-colors">
              <FileText className="h-5 w-5 text-gray-600" />
            </div>
            <p className="text-sm font-bold text-gray-900">Form view</p>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Detailed entry with all fields visible.
            </p>
          </button>
        </div>

        <div className="px-5 pb-5">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <span
              className={`h-5 w-5 rounded-md border flex items-center justify-center transition-colors ${
                always ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
              }`}
            >
              {always && <Check className="h-3.5 w-3.5 text-white" />}
            </span>
            <input
              type="checkbox"
              className="sr-only"
              checked={always}
              onChange={e => setAlways(e.target.checked)}
            />
            <span className="text-sm text-gray-600">Always use this view</span>
          </label>
        </div>
      </div>
    </div>
  )
}
